/**
 * Question Modes Registry — formal mode conventions per question type.
 *
 * Established as part of TAA Audit S0 (2026-05).
 *
 * Background:
 * Several question types ship with renderer-level "modes" controlled via
 * `data.mode` on the seed (e.g. text_highlight `tap_word_class`,
 * multiple_choice `icon_chips`). Until now these mode strings lived only as
 * string literals inside renderers — which made them prone to typo-drift,
 * impossible to validate at the seed level, and undocumented for seed authors.
 *
 * This module is the single source of truth.
 *
 * Rules:
 * 1. A renderer MUST accept the absence of `data.mode` and fall back to its
 *    default (legacy) behaviour. `data.mode` is always optional.
 * 2. When `data.mode` is set, it MUST be one of the strings listed here for
 *    that question type. Otherwise the governance pipeline emits
 *    `TYPE_INVALID_MODE` (coach severity, level 50).
 * 3. Adding a new mode = add it here AND update the matching renderer AND
 *    update the audit doc / question-type spec.
 */

// ─── text_highlight modes ──────────────────────────────────────────────
//
// - default: render `data.text` + `data.correctHighlights` OR
//   `data.segments`. Multi-select activates when >1 segment is correct.
// - tap_word_class: highlight all words of a given word class in a
//   sentence. Requires `data.sentence` + `data.tappableWords[]` +
//   `data.targetClass` (display label).
// - tap_referent: tap the referent of a pronoun in a passage. Requires
//   `data.passage` + `data.pronounIndex` + `data.candidateIndices[]` +
//   `data.correctIndex`. Forced single-select.
export const TEXT_HIGHLIGHT_MODES = ['tap_word_class', 'tap_referent'] as const;
export type TextHighlightMode = (typeof TEXT_HIGHLIGHT_MODES)[number];

// ─── multiple_choice modes ─────────────────────────────────────────────
//
// - default: vertical stacked option list with letter prefix (A/B/C/D).
// - icon_chips: 2x2 (or 4x1) grid of icon-chip cards. Requires
//   `data.optionIcons[]` parallel array (lucide-react icon names) indexed
//   by originalIndex of `question.options`.
// - two_step_judge: two-stage MC. Step 1 is a binary judge (e.g.
//   letterlijk vs figuurlijk); when the learner picks the configured
//   "trigger" answer the renderer transitions to a regular MC step 2
//   for the meaning. Requires `data.step1` + `data.step2` +
//   `data.step2_ifIndex` (the index in step1.options that opens step 2).
//   Used by TAA Q4 idiom_literal_judge.
export const MULTIPLE_CHOICE_MODES = ['icon_chips', 'two_step_judge'] as const;
export type MultipleChoiceMode = (typeof MULTIPLE_CHOICE_MODES)[number];

// ─── drag_drop_fill modes ──────────────────────────────────────────────
//
// - default: template + blanks/draggables, all options visible at once.
// - with_bank: same data shape but UI emphasises the "word bank" — used
//   for cloze questions where the bank itself is a teaching device.
//   Reserved for TAA S6; renderer support pending.
export const DRAG_DROP_FILL_MODES = ['with_bank'] as const;
export type DragDropFillMode = (typeof DRAG_DROP_FILL_MODES)[number];

// ─── Validator helpers ─────────────────────────────────────────────────

const MODES_BY_TYPE: Record<string, readonly string[]> = {
  text_highlight: TEXT_HIGHLIGHT_MODES,
  multiple_choice: MULTIPLE_CHOICE_MODES,
  drag_drop_fill: DRAG_DROP_FILL_MODES
};

/**
 * Returns the valid mode set for a question type, or null if the type
 * has no registered modes (i.e. setting data.mode is unsupported).
 */
export function getValidModesFor(questionType: string): readonly string[] | null {
  return MODES_BY_TYPE[questionType] ?? null;
}

/**
 * Returns true when `mode` is a registered mode for `questionType`.
 * `mode` of `undefined`/`null` is always considered valid (= default).
 */
export function isValidMode(questionType: string, mode: string | undefined | null): boolean {
  if (mode == null) return true;
  const valid = MODES_BY_TYPE[questionType];
  if (!valid) return false;
  return valid.includes(mode);
}

/**
 * Type-safe accessor for `question.data.mode`.
 *
 * Reads the `mode` field off an unknown-shaped question/data container and
 * narrows it to one of the supplied `validModes` literals. Returns
 * `undefined` when the field is missing, not a string, or unknown — which
 * the calling renderer should interpret as "use default rendering".
 *
 * Replaces the `(question as any).data?.mode` pattern that previously lived
 * in three renderers (TAA S6 hardening, P1-2).
 *
 * @example
 *   const mode = readMode(question, MULTIPLE_CHOICE_MODES)
 *   if (mode === 'two_step_judge') { ... }
 */
export function readMode<T extends string>(question: {
  data?: unknown;
} | undefined | null, validModes: readonly T[]): T | undefined {
  if (!question) return undefined;
  const data = (question as {
    data?: unknown;
  }).data;
  if (!data || typeof data !== 'object') return undefined;
  const m = (data as Record<string, unknown>).mode;
  if (typeof m !== 'string') return undefined;
  return (validModes as readonly string[]).includes(m) ? m as T : undefined;
}